import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import BadgePills from "@/components/BadgePills";
import { Loader2, Phone, Navigation, MessageCircle, AlertCircle } from "lucide-react";

const POLL_MS = 15_000;

/**
 * Premium "driver accepted" card for the hotel history list.
 * Replaces the compressed text line + small ETA badge with a high-density,
 * visually-striking block surfacing:
 *   • Huge live ETA (driver → hotel, recomputed every 15s)
 *   • Driver name + phone CTA
 *   • Vehicle brand + model, prominent French license plate
 *   • Optional "chat" CTA
 * The component fetches its own ETA so the parent only needs to pass the
 * booking object.
 */
export default function BookingAcceptedCard({ booking, onOpenChat }) {
  const [eta, setEta] = useState({ loading: true, data: null, error: false });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!booking?.id) return;
    let cancelled = false;
    const fetchEta = async () => {
      try {
        const { data } = await api.get(`/hotel/bookings/${booking.id}/driver-eta`);
        if (!cancelled) setEta({ loading: false, data, error: false });
      } catch (_) {
        if (!cancelled) setEta((s) => ({ ...s, loading: false, error: true }));
      }
    };
    fetchEta();
    timerRef.current = setInterval(fetchEta, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
    };
  }, [booking?.id]);

  const d = eta.data || {};
  const isLive = d.has_gps || (d.is_manual && d.eta_minutes != null);
  const min = d.eta_minutes;

  const initials = (booking.driver_name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-primary/25 bg-gradient-to-br from-primary/8 via-card to-card shadow-sm"
      data-testid={`accepted-card-${booking.id}`}
    >
      {/* subtle pulsing dot strip on the left edge */}
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-primary via-primary/60 to-primary/20" />

      <div className="grid gap-5 p-5 sm:p-6 md:grid-cols-[auto_1fr_auto] md:items-center">
        {/* ── ETA block (huge) ─────────────────────────────────── */}
        <div className="flex items-center gap-4 md:flex-col md:items-start md:gap-1">
          <div className="flex items-baseline gap-1.5 leading-none">
            {eta.loading ? (
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
            ) : !d.has_driver ? (
              <span className="font-[Outfit] text-3xl font-semibold text-muted-foreground">—</span>
            ) : !isLive ? (
              <span className="inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--warning))]">
                <AlertCircle className="h-4 w-4" /> GPS indispo.
              </span>
            ) : min == null ? (
              <span className="font-[Outfit] text-3xl font-semibold text-muted-foreground">—</span>
            ) : min <= 0 ? (
              <span
                className="font-[Outfit] text-4xl font-semibold text-primary sm:text-5xl"
                data-testid={`accepted-eta-${booking.id}`}
              >
                Arrive
              </span>
            ) : (
              <>
                <span
                  className="font-[Outfit] text-5xl font-semibold tracking-tight text-primary sm:text-6xl"
                  data-testid={`accepted-eta-${booking.id}`}
                >
                  {min}
                </span>
                <span className="font-[Outfit] text-xl font-medium text-primary/80 sm:text-2xl">
                  min
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-primary/70">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            Chauffeur en approche
          </div>
        </div>

        {/* ── Driver identity ──────────────────────────────────── */}
        <div className="flex min-w-0 items-center gap-3 border-y border-border/60 py-4 md:border-x md:border-y-0 md:px-5 md:py-0">
          <div className="grid h-12 w-12 flex-none place-items-center rounded-full bg-primary/15 font-[Outfit] text-base font-semibold text-primary ring-2 ring-primary/20">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Chauffeur</p>
            <p
              className="truncate font-[Outfit] text-base font-semibold text-foreground sm:text-lg"
              data-testid={`accepted-driver-${booking.id}`}
            >
              {booking.driver_name || "—"}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
              {booking.driver_phone && (
                <a
                  href={`tel:${booking.driver_phone}`}
                  className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
                  data-testid={`accepted-phone-${booking.id}`}
                >
                  <Phone className="h-3 w-3" />
                  {booking.driver_phone}
                </a>
              )}
              {onOpenChat && (
                <button
                  type="button"
                  onClick={() => onOpenChat(booking)}
                  className="inline-flex items-center gap-1 font-medium text-foreground/70 underline-offset-4 hover:text-primary hover:underline"
                  data-testid={`accepted-chat-${booking.id}`}
                >
                  <MessageCircle className="h-3 w-3" />
                  Chat
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Vehicle + license plate ──────────────────────────── */}
        <div className="flex flex-col gap-2 md:items-end">
          {booking.driver_vehicle_brand_model && (
            <p
              className="font-[Outfit] text-lg font-semibold text-foreground sm:text-xl md:text-right"
              data-testid={`accepted-vehicle-${booking.id}`}
            >
              {booking.driver_vehicle_brand_model}
            </p>
          )}
          {booking.driver_license_plate && (
            <LicensePlate value={booking.driver_license_plate} bookingId={booking.id} />
          )}
          {!booking.driver_vehicle_brand_model && !booking.driver_license_plate && (
            <span className="text-xs text-muted-foreground">Véhicule non renseigné</span>
          )}
          {booking.driver_badges && booking.driver_badges.length > 0 && (
            <div className="md:self-end">
              <BadgePills badges={booking.driver_badges} size="sm" />
            </div>
          )}
        </div>
      </div>

      {/* footer thin: action shortcut */}
      <div className="flex items-center justify-between gap-3 border-t border-border/60 bg-background/30 px-5 py-2.5 text-[11px] text-muted-foreground sm:px-6">
        <span className="inline-flex items-center gap-1.5">
          <Navigation className="h-3 w-3 text-primary" />
          ETA recalculé toutes les 15 s depuis la position GPS du chauffeur
        </span>
        <span className="hidden sm:inline-flex items-center gap-1.5 font-medium text-foreground/70">
          {booking.client_name} · {booking.passenger_count} pers · {booking.price} €
        </span>
      </div>
    </div>
  );
}

function LicensePlate({ value, bookingId }) {
  return (
    <div
      className="inline-flex overflow-hidden rounded-lg border-[3px] border-neutral-900 bg-white font-mono text-xl font-extrabold tracking-[0.22em] text-neutral-900 shadow-md sm:text-2xl"
      data-testid={`accepted-plate-${bookingId}`}
    >
      <span className="flex min-w-[28px] items-end justify-center bg-[#003399] px-1.5 pb-1 pt-3 text-[10px] font-bold leading-none text-yellow-300">
        F
      </span>
      <span className="px-3.5 py-1.5">{value}</span>
    </div>
  );
}
